import { Module, Mutation, Action, VuexModule } from "vuex-module-decorators";
import { SMAdditionalInfo, SMNps, SMQuestionnaire, SMUser } from "@/store";
import { TAdditional_Info } from "@/models/additional_info";
import { API_ROUTES } from "@/utils/API";
import axios from "@/plugins/http";
import router from "@/router";
import { API_TDependant } from "@/models/dependant";

const CANDIDATES_PATH = "/employer/visa-cases";
const SERVICES_CANDIDATES_PATH = "/employer/services/candidates";
const SERVICES_EXTENSION_PATH = "/employer/services/extension";
import { CANDIDATE_ASSESSMENT_SERVICE, START_SERVICE_EVENT } from "@/constants/services";
import { REmployerServicesCandidates } from "@/pages/Layout/views/Employer/views/ServicesCandidates/REmployerServicesCandidates";
import { REmployerCandidateInvite } from "@/pages/Layout/views/Employer/views/CandidateInvite/RCandidateInvite";

@Module({ name: "MAdditionalInfo", namespaced: true })
export default class MAdditionalInfo extends VuexModule {
  homePageAdditionalInfo: TAdditional_Info | null = null;

  @Action
  async storeHomePageAdditionalInfo(payload) {
    await this.context.commit("setHomePageAdditionalInfo", payload);
  }

  @Mutation
  setHomePageAdditionalInfo(payload) {
    this.homePageAdditionalInfo = payload;
  }

  get currentHomePageActualInfo() {
    return this.homePageAdditionalInfo;
  }

  get isOnboardingCompleted(): boolean {
    return (
      this.homePageAdditionalInfo?.banner?.employer_completed_account_setup_onboarding || false
    );
  }

  get onboardingTaskId(): number {
    return (
      Number(this.homePageAdditionalInfo?.banner?.account_setup_onboarding_task_user_flow_id) || 0
    );
  }

  get hasOnboardingTask(): boolean {
    return this.onboardingTaskId > 0;
  }

  @Action
  async initUserOnboarding() {
    const userFlowId = this.onboardingTaskId;

    if (!userFlowId) return;

    await SMQuestionnaire.openQuestionnaire(userFlowId);
  }

  get showReportAndInviteGroupQuickActionsButtons() {
    // return this.isOnboardingCompleted && !SMUser.isComplianceBlocked;

    return SMUser.isSponsorConfirmation && !SMUser.isComplianceBlocked;
  }

  get quickActions() {
    return [
      {
        label: "Start New Visa",
        icon: "file-certificate",
        info: "Initiate the process of applying for a Skilled Worker visa",
        method: SMAdditionalInfo.startSWQuickAction,
        show: true,
        disabled: !SMUser.isSponsor || this.hasOnboardingTask,
      },
      {
        label: "Extend Visa",
        icon: "ExtendVisa",
        info: "Extend Skilled Worker visa for your existing employee on the platform",
        method: SMAdditionalInfo.startSWExtendQuickAction,
        show: true,
        disabled: !SMUser.isSponsor || this.hasOnboardingTask,
      },
      {
        label: "Increase CoS Allocation",
        icon: "device-mobile-message",
        info:
          "Request additional Certificates of Sponsorship (CoS) in the Sponsor Management System (SMS)",
        method: SMAdditionalInfo.cosAllocationRequestQuickAction,
        show: true,
        disabled: !(SMUser.isSponsor && SMUser.isSponsorConfirmation),
      },
      {
        label: "Assign CoS",
        icon: "file-like",
        info: "Assign a Certificate of Sponsorship to a selected candidate",
        method: SMAdditionalInfo.assignCosQuickAction,
        show: true,
        disabled: !(SMUser.isSponsor && SMUser.isSponsorConfirmation),
      },
      {
        label: "Mock Audit",
        icon: "checks",
        info:
          "Set up a mock audit to ensure your sponsorship duties and records meet compliance requirements",
        method: SMAdditionalInfo.mockAuditQuickAction,
        show: true,
        disabled: !(SMUser.isSponsor && SMUser.isSponsorConfirmation),
      },
      {
        label: "Paid Consultation",
        icon: "messages",
        info: "Book a paid consultation with an immigration expert for tailored advice",
        method: SMAdditionalInfo.paidConsultationQuickAction,
        show: !SMUser.isPremiumSubscription && !SMUser.isStandardSubscription,
        disabled: false,
      },
      {
        label: "Report Change in SMS",
        icon: "file-pencil",
        info:
          "Notify the Home Office of any changes related to the company or sponsored employees via SMS",
        method: SMAdditionalInfo.reportChangeInSMSQuickAction,
        show: true,
        disabled: !this.showReportAndInviteGroupQuickActionsButtons,
      },
      {
        label: "Start Right to Work Check",
        icon: "file-upload",
        info: "Conduct a compliant Right to Work Check for an employee",
        method: SMAdditionalInfo.startRTWCQuickAction,
        show: false,
        disabled: !this.showReportAndInviteGroupQuickActionsButtons,
      },
      {
        label: "Report Employee as a Leaver",
        icon: "user-minus",
        info:
          "Inform the Home Office when an employee is about to leave or has left the organisation",
        method: SMAdditionalInfo.reportAsAsLeaverQuickAction,
        show: true,
        disabled: !this.showReportAndInviteGroupQuickActionsButtons,
      },
      {
        label: "Invite Employee for Compliance",
        icon: "user-plus",
        info: "Invite an employee to the platform to manage Compliance",
        method: SMAdditionalInfo.inviteEmployeeForComplianceQuickAction,
        show: true,
        disabled: SMUser.isComplianceBlocked || !SMUser.isSponsor || this.hasOnboardingTask,
      },
      {
        label: "Guides",
        icon: "book",
        info: "Learn how to use the platform and meet compliance duties",
        method: SMAdditionalInfo.openGuidesQuickAction,
        show: true,
        disabled: false,
      },
    ];
  }

  get filteredQuickActions() {
    return this.quickActions.filter((item) => item.show);
  }

  get quickActionsEmployee() {
    return [
      {
        label: "Buy Skilled Worker Dependant visa",
        icon: "users",
        method: SMAdditionalInfo.buySWDependantVisa,
        show: this.canStartDependantCase,
        disabled: false,
      },
      {
        label: "Buy Indefinite Leave to Remain (ILR)",
        icon: "file-certificate",
        method: SMAdditionalInfo.buyILR,
        show: true,
        disabled: false,
      },
      {
        label: "Apply for British Citizenship",
        icon: "file-upload",
        method: SMAdditionalInfo.applyBritishCitizenship,
        show: true,
        disabled: false,
      },
    ];
  }

  get filteredQuickActionsEmployee() {
    return this.quickActionsEmployee.filter((item) => item.show);
  }

  // ACTIONS
  @Action
  async startSWQuickAction() {
    try {
      await router.push(REmployerServicesCandidates);
    } catch {
      return;
    }
  }

  @Action
  async startSWExtendQuickAction() {
    try {
      await router.push(SERVICES_EXTENSION_PATH);
    } catch {
      return;
    }
  }

  @Action
  async cosAllocationRequestQuickAction() {
    this.context.commit("openPricinServicePopop", "cos_allocation");
  }

  @Action
  async assignCosQuickAction() {
    try {
      await router.push(SERVICES_CANDIDATES_PATH);
    } catch {
      return;
    }
  }

  @Action
  async mockAuditQuickAction() {
    this.context.commit("openPricinServicePopop", "mock_audit");
  }

  @Action
  async paidConsultationQuickAction() {
    this.context.commit("openPricinServicePopop", "paid_consultation");
  }

  @Action
  async reportChangeInSMSQuickAction() {
    try {
      const { data } = await axios.get<string>(API_ROUTES.COMPANY_REPORT_CHANGE);

      if (!data) return;

      const id = Number(data);

      if (!id) return;

      await SMQuestionnaire.openQuestionnaire(id);

      await SMNps.removeFromLocalStorage("report_change");
    } catch (e) {
      console.log("ReportChangePopup error:", e);
    }
  }

  @Action
  async startRTWCQuickAction() {
    try {
      await router.push(CANDIDATES_PATH);
    } catch {
      return;
    }
  }

  @Action
  async reportAsAsLeaverQuickAction() {
    try {
      await router.push("/employer/employees");
    } catch {
      return;
    }
  }

  @Action
  async inviteEmployeeForComplianceQuickAction() {
    const { data } = await axios.get(API_ROUTES.COMPANY_EMPLOYEE_ONBOARD_EMPLOYEE);

    if (!data) return;

    const { task_id } = data;

    await router.push(SMUser.basePath + `/tasks/${task_id}`);
  }

  @Action
  async openGuidesQuickAction() {
    try {
      await router.push("/employer/guide");
    } catch {
      return;
    }
  }

  pricingServicesActiveService: "cos_allocation" | "mock_audit" | "paid_consultation" | null = null;
  pricingServicesCurrentStep: "intro" | "form" | "success" = "intro";
  pricingServicesShowModal = false;

  @Mutation
  openPricinServicePopop(service: "cos_allocation" | "mock_audit" | "paid_consultation") {
    this.pricingServicesActiveService = service;
    this.pricingServicesCurrentStep = "intro";
    this.pricingServicesShowModal = true;
  }

  @Mutation
  setPricingServicesCurrentStep(step: "intro" | "form" | "success") {
    this.pricingServicesCurrentStep = step;
  }

  @Mutation
  setPricingServicesActiveService(
    service: "cos_allocation" | "mock_audit" | "paid_consultation" | null
  ) {
    this.pricingServicesActiveService = service;
  }

  @Mutation
  setPricingServicesShowModal(value: boolean) {
    this.pricingServicesShowModal = value;
  }

  pricingRequestsActiveService: "sl" | "sw_dependant" | "ilr" | "citizenship" | null = null;
  pricingRequestsCurrentStep: "intro" | "success" = "intro";
  pricingRequestsShowModal = false;

  @Mutation
  openPricingRequestsPopup(service: "sl" | "sw_dependant" | "ilr" | "citizenship") {
    this.pricingRequestsActiveService = service;
    this.pricingRequestsCurrentStep = "intro";
    this.pricingRequestsShowModal = true;
  }

  @Mutation
  setPricingRequestsCurrentStep(step: "intro" | "success") {
    this.pricingRequestsCurrentStep = step;
  }

  @Mutation
  setPricingRequestsActiveService(service: "sl" | "sw_dependant" | "ilr" | "citizenship" | null) {
    this.pricingRequestsActiveService = service;
  }

  @Mutation
  setPricingRequestsShowModal(value: boolean) {
    this.pricingRequestsShowModal = value;
  }

  @Action
  async buySWDependantVisa() {
    this.context.commit("openPricingRequestsPopup", "sw_dependant");
  }

  @Action
  async buyILR() {
    this.context.commit("openPricingRequestsPopup", "ilr");
  }

  @Action
  async applyBritishCitizenship() {
    this.context.commit("openPricingRequestsPopup", "citizenship");
  }

  @Action
  async inviteCandidate() {
    const query = {
      event: START_SERVICE_EVENT,
      service: CANDIDATE_ASSESSMENT_SERVICE,
    };

    await router.push({
      name: REmployerCandidateInvite.name,
      query,
    });
  }

  dependantsList: API_TDependant[] = [];

  @Mutation
  setDependantsList(value: API_TDependant[]) {
    this.dependantsList = value;
  }

  canStartDependantCase = false;

  @Mutation
  setCanStartDependantCase(value: boolean) {
    this.canStartDependantCase = value;
  }

  @Action
  async fetchDependantsList() {
    try {
      const { data } = await axios.get<API_TDependant[]>(API_ROUTES.GET_DEPENDANT());

      if (!data) return;

      this.context.commit("setDependantsList", data);

      this.context.commit(
        "setCanStartDependantCase",
        !!data.filter((d) => !d.case_created).length || false
      );
    } catch (e) {
      console.log("fetchDependantsList error:", e);
    }
  }
}
